/**
 * components.tsx — 设计系统活文档「组件库」分区
 *
 * 移植自原型 design/Novel-admin/src/pages/design-system-components.jsx
 * （Button / Input / Select / Switch / Chip / Card / Table / FormRow /
 *  Dialog / Toast / 多语言字段 / 状态机）。
 * 原型用 window.__dsComponentSections 注册；这里改为具名 ESM export。
 * window.Icons → import { Icons }；多语言示例改用冻结的受控
 * <MultiLangFormRows>（原型的 window.__multiLang.{useMultiLang,OtherLangsField}
 * 已统一收敛为该受控组件，故此处用本地 useState 持有 value）。
 */

import { useState } from 'react';
import { useSysConfig } from '~/data/queries';
import { Icons } from '~/lib/icons';
import { MultiLangFormRows, OtherLangsField } from '~/ui/MultiLangFormRows';
import { Table } from '~/ui/Table';
import { DSBlock, DSDemo, DSHeader, DSRule } from './ds-primitives';

// ============================================================
// Button
// ============================================================
export function DSCButton() {
  return (
    <>
      <DSHeader title="Button" sub="五种风格 × 三种尺寸，覆盖全部操作场景" />
      <DSBlock title="风格">
        <DSDemo
          title="primary / accent / default / ghost / danger"
          code={`<button className="btn btn-primary">保存</button>
<button className="btn btn-accent">新建</button>
<button className="btn">取消</button>
<button className="btn btn-ghost">查看</button>
<button className="btn btn-danger">删除</button>`}
        >
          <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary">
              主操作 (primary)
            </button>
            <button type="button" className="btn btn-accent">
              <Icons.IconPlus size={12} />
              强调 (accent)
            </button>
            <button type="button" className="btn">
              默认
            </button>
            <button type="button" className="btn btn-ghost">
              幽灵
            </button>
            <button type="button" className="btn btn-danger">
              <Icons.IconTrash size={11} />
              危险
            </button>
          </div>
        </DSDemo>
      </DSBlock>
      <DSBlock title="尺寸">
        <DSDemo
          title="sm / 默认 / lg"
          code={`<button className="btn btn-sm">小</button>
<button className="btn">中</button>
<button className="btn btn-lg">大</button>`}
        >
          <div className="hstack" style={{ gap: 8 }}>
            <button type="button" className="btn btn-sm">
              小 (sm)
            </button>
            <button type="button" className="btn">
              默认
            </button>
            <button type="button" className="btn btn-lg">
              大 (lg)
            </button>
          </div>
        </DSDemo>
      </DSBlock>
      <DSBlock title="图标按钮">
        <DSDemo
          code={`<button className="btn btn-icon"><Icons.IconEdit size={12}/></button>
<button className="btn btn-ghost btn-icon"><Icons.IconMoreV size={13}/></button>`}
        >
          <div className="hstack" style={{ gap: 8 }}>
            <button type="button" className="btn btn-icon">
              <Icons.IconEdit size={12} />
            </button>
            <button type="button" className="btn btn-icon">
              <Icons.IconCopy size={12} />
            </button>
            <button type="button" className="btn btn-icon">
              <Icons.IconTrash size={12} />
            </button>
            <button type="button" className="btn btn-ghost btn-icon">
              <Icons.IconMoreV size={13} />
            </button>
          </div>
        </DSDemo>
      </DSBlock>
      <DSRule tone="do">每个页面 / 弹窗最多 1 个 primary 或 accent 按钮</DSRule>
      <DSRule tone="dont">不要在卡片右上角放 danger 按钮 — 用 ContextMenu 收起来</DSRule>
      <DSRule tone="note">破坏性操作（删除 / 下架）必须走二次确认弹窗</DSRule>
    </>
  );
}

// ============================================================
// Input / Textarea
// ============================================================
export function DSCInput() {
  return (
    <>
      <DSHeader title="Input · Textarea" sub="单一 .input 类承担所有文本输入" />
      <DSBlock title="基本">
        <DSDemo
          code={`<input className="input" placeholder="书名"/>
<input className="input mono" placeholder="ID · bk_xxxx"/>
<textarea className="input" rows={3} placeholder="简介…"/>`}
        >
          <div className="vstack" style={{ gap: 8, maxWidth: 380 }}>
            <input className="input" placeholder="标准输入框" />
            <input className="input mono" placeholder="mono 字体 · 输入 ID / 数字" />
            <textarea className="input" rows={2} placeholder="多行 · textarea" />
            <input
              className="input"
              placeholder="禁用态"
              disabled
              style={{ background: 'var(--bg-subtle)' }}
            />
          </div>
        </DSDemo>
      </DSBlock>
      <DSBlock title="搜索框">
        <DSDemo
          desc="SearchInput 包装在 ~/ui/SearchInput，含左侧 ⌘K 快捷键提示"
          code={`<SearchInput placeholder="搜索…" width={280}/>`}
        >
          <div style={{ position: 'relative', maxWidth: 280 }}>
            <Icons.IconSearch
              size={12}
              style={{ position: 'absolute', left: 10, top: 8, color: 'var(--fg-faint)' }}
            />
            <input
              className="input"
              placeholder="搜索…"
              style={{ paddingLeft: 28, paddingRight: 36 }}
            />
            <span className="kbd" style={{ position: 'absolute', right: 8, top: 5 }}>
              ⌘K
            </span>
          </div>
        </DSDemo>
      </DSBlock>
      <DSRule tone="note">label 单独写在外面（用 FormRow），不要给 input 加左侧 label 文本</DSRule>
    </>
  );
}

// ============================================================
// Select / Filter
// ============================================================
export function DSCSelect() {
  return (
    <>
      <DSHeader
        title="Select · FilterButton"
        sub="原生 select 用 .input 类；带搜索 / 多选的下拉用 FilterButton"
      />
      <DSBlock title="基本 Select">
        <DSDemo
          code={`<select className="input">
  <option>选项 A</option>
  <option>选项 B</option>
</select>`}
        >
          <select className="input" style={{ maxWidth: 240 }}>
            <option>奇幻</option>
            <option>悬疑</option>
            <option>科幻</option>
          </select>
        </DSDemo>
      </DSBlock>
      <DSBlock title="FilterButton" sub="多选 / 可搜索 / 带 apply-clear / 用于列表筛选条">
        <DSDemo
          code={`<FilterButton label="语言" value={['en','de']} options={[...]} searchable/>`}
        >
          <div className="hstack" style={{ gap: 8 }}>
            <button type="button" className="btn btn-sm">
              <Icons.IconFilter size={11} />
              分类{' '}
              <span className="chip" style={{ fontSize: 9, padding: '0 5px', marginLeft: 4 }}>
                2
              </span>
              <Icons.IconChevDown size={11} />
            </button>
            <button type="button" className="btn btn-sm">
              <Icons.IconFilter size={11} />
              语言
              <Icons.IconChevDown size={11} />
            </button>
          </div>
        </DSDemo>
      </DSBlock>
    </>
  );
}

// ============================================================
// Switch / Segmented
// ============================================================
export function DSCSwitch() {
  const [on, setOn] = useState(true);
  const [seg, setSeg] = useState('grid');
  return (
    <>
      <DSHeader title="Switch · Segmented" sub="开关二态；分段控制 2–4 个互斥选项" />
      <DSBlock title="Switch">
        <DSDemo code={'<Switch on={true} onChange={...}/>'}>
          <div className="hstack" style={{ gap: 14 }}>
            <button
              type="button"
              role="switch"
              aria-checked={on}
              onClick={() => setOn(!on)}
              style={{
                width: 30,
                height: 18,
                borderRadius: 999,
                background: on ? 'var(--accent)' : 'var(--bg-inset)',
                border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                position: 'relative',
                padding: 0,
                cursor: 'pointer',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: 1,
                  left: on ? 13 : 1,
                  width: 14,
                  height: 14,
                  borderRadius: 999,
                  background: 'white',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
                  transition: 'left .15s',
                }}
              />
            </button>
            <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
              {on ? '已启用' : '已关闭'}
            </span>
          </div>
        </DSDemo>
      </DSBlock>
      <DSBlock title="Segmented">
        <DSDemo
          code={`<div className="seg">
  <button className="on">A</button>
  <button>B</button>
</div>`}
        >
          <div className="seg">
            {(
              [
                { id: 'grid', icon: 'IconLayout' },
                { id: 'list', icon: 'IconList' },
                { id: 'card', icon: 'IconApps' },
              ] as const
            ).map((o) => {
              const Icon = Icons[o.icon];
              return (
                <button
                  type="button"
                  key={o.id}
                  className={seg === o.id ? 'on' : ''}
                  onClick={() => setSeg(o.id)}
                >
                  <Icon size={11} />
                  {o.id}
                </button>
              );
            })}
          </div>
        </DSDemo>
      </DSBlock>
      <DSRule tone="note">Switch 用于布尔开关；3 个以上选项必用 Segmented 或 Radio</DSRule>
    </>
  );
}

// ============================================================
// Chip / Tag / Badge
// ============================================================
export function DSCChip() {
  return (
    <>
      <DSHeader title="Chip · Tag · Badge" sub="小型状态 / 元数据标识，圆角 999px，五种语义" />
      <DSBlock title="状态">
        <DSDemo
          code={`<span className="chip chip-pos">上架</span>
<span className="chip chip-warn">隐藏</span>
<span className="chip chip-neg">下架</span>
<span className="chip chip-info">同步中</span>
<span className="chip chip-accent">VIP</span>
<span className="chip">默认</span>`}
        >
          <div className="hstack" style={{ gap: 6, flexWrap: 'wrap' }}>
            <span className="chip chip-pos">● 上架</span>
            <span className="chip chip-warn">● 隐藏</span>
            <span className="chip chip-neg">● 下架</span>
            <span className="chip chip-info">同步中</span>
            <span className="chip chip-accent">VIP</span>
            <span className="chip">默认</span>
          </div>
        </DSDemo>
      </DSBlock>
      <DSBlock title="带图标 / 数字">
        <DSDemo
          code={`<span className="chip"><span className="mono">ZH</span><span>中文</span></span>`}
        >
          <div className="hstack" style={{ gap: 6, flexWrap: 'wrap' }}>
            <span className="chip">
              <Icons.IconGlobe size={10} />
              DE 德国
            </span>
            <span className="chip">
              <span className="mono">ZH</span>
              <span style={{ color: 'var(--fg-faint)', marginLeft: 3 }}>中文</span>
            </span>
            <span className="chip chip-info">
              <Icons.IconLanguages size={10} />
              反馈语言 DE
            </span>
            <span className="chip mono" style={{ fontSize: 10 }}>
              +3
            </span>
          </div>
        </DSDemo>
      </DSBlock>
      <DSRule tone="do">
        语言 chip 一律「mono CODE + 浅色 中文」格式：<code>ZH 中文 / DE 德语</code>
      </DSRule>
      <DSRule tone="do">
        地区 chip 一律「mono CODE + 中文」格式：<code>DE 德国 / FR 法国</code>
      </DSRule>
      <DSRule tone="dont">不要混用原生名（Deutsch / Français）— 已统一为中文</DSRule>
    </>
  );
}

// ============================================================
// Card / Stat
// ============================================================
export function DSCCard() {
  return (
    <>
      <DSHeader title="Card · Stat" sub="两层卡片（card / card-flat）+ 一种 KPI Stat 结构" />
      <DSBlock title="Card 层级">
        <DSDemo
          code={`<div className="card"></div>        {/* 主卡片 · 圆角 8 + 阴影 */}
<div className="card-flat"></div>   {/* 平卡片 · 圆角 6 + 无阴影 */}`}
        >
          <div className="hstack" style={{ gap: 12 }}>
            <div className="card" style={{ padding: 16, flex: 1 }}>
              <div className="label" style={{ marginBottom: 4 }}>
                主卡片
              </div>
              <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                有阴影 · 圆角 8 · 重要内容容器
              </div>
            </div>
            <div className="card-flat" style={{ padding: 16, flex: 1 }}>
              <div className="label" style={{ marginBottom: 4 }}>
                平卡片
              </div>
              <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                无阴影 · 圆角 6 · 列表项 / 提示框
              </div>
            </div>
          </div>
        </DSDemo>
      </DSBlock>
      <DSBlock title="Stat · KPI 卡片">
        <DSDemo
          code={`<div className="card stat">
  <div className="stat-label"><Icons.IconWallet/>累计收入</div>
  <div className="stat-value">€1.14M</div>
  <div className="stat-delta delta-pos">↗ 12.4%</div>
</div>`}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {[
              { label: '累计收入', v: '€1.14M', d: '+12.4%', tone: 'pos' },
              { label: 'DAU', v: '24,128', d: '+3.6%', tone: 'pos' },
              { label: '退款率', v: '0.87%', d: '-0.3%', tone: 'pos' },
            ].map((s) => (
              <div key={s.label} className="card stat">
                <div className="stat-label">
                  <Icons.IconChart size={11} />
                  {s.label}
                </div>
                <div className="stat-value">{s.v}</div>
                <div className={`stat-delta delta-${s.tone}`}>
                  <Icons.IconArrowUp size={9} />
                  {s.d}
                  <span style={{ color: 'var(--fg-faint)', marginLeft: 4 }}>vs 上周</span>
                </div>
              </div>
            ))}
          </div>
        </DSDemo>
      </DSBlock>
    </>
  );
}

// ============================================================
// Table
// ============================================================
export function DSCTable() {
  return (
    <>
      <DSHeader title="Table" sub="共享 Table · 可拖动列宽 + 紧凑表头 + sticky + 行 hover" />
      <DSBlock title="基本表格">
        <DSDemo
          code={`<Table>
  <thead><tr><th>列</th></tr></thead>
  <tbody><tr><td>...</td></tr></tbody>
</Table>`}
        >
          <div className="card" style={{ overflow: 'hidden' }}>
            <Table className="tbl">
              <thead>
                <tr>
                  <th>书名</th>
                  <th>分类</th>
                  <th>地区</th>
                  <th>状态</th>
                  <th className="num">章数</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { t: '深渊之约', c: '悬疑', m: 'DE 德国 · FR 法国', s: '上架', n: 128 },
                  { t: '霜与硫黄', c: '奇幻', m: 'ES 西班牙', s: '隐藏', n: 96 },
                  { t: '南港之夜', c: '言情', m: 'DE 德国 · IT 意大利', s: '下架', n: 64 },
                ].map((r) => (
                  <tr key={r.t}>
                    <td>{r.t}</td>
                    <td>
                      <span className="chip" style={{ fontSize: 10 }}>
                        {r.c}
                      </span>
                    </td>
                    <td className="mono" style={{ fontSize: 11 }}>
                      {r.m}
                    </td>
                    <td>
                      <span
                        className={`chip ${r.s === '上架' ? 'chip-pos' : r.s === '下架' ? 'chip-neg' : 'chip-warn'}`}
                      >
                        ● {r.s}
                      </span>
                    </td>
                    <td className="num">{r.n}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </DSDemo>
      </DSBlock>
      <DSRule tone="do">
        数字列加 <code>className="num"</code> — 自动右对齐 + mono 字体 + 表格数字
      </DSRule>
      <DSRule tone="note">表头 sticky；hover 行背景变 bg-subtle</DSRule>
      <DSRule tone="note">
        拖动表头右边界调整列宽；方向键微调、Shift 加速；双击恢复默认列宽。
      </DSRule>
    </>
  );
}

// ============================================================
// FormRow / Section
// ============================================================
export function DSCFormRow() {
  return (
    <>
      <DSHeader title="FormRow · Section" sub="标准表单结构 · 左侧 140px label + 右侧填字段" />
      <DSBlock title="结构">
        <DSDemo
          code={`<Section title="基础信息" sub="…">
  <FormRow label="书名" required hint="必填">
    <input className="input"/>
  </FormRow>
</Section>`}
        >
          <div className="card" style={{ padding: 16 }}>
            <div style={{ marginBottom: 14 }}>
              <h3 style={{ margin: 0, font: 'inherit', fontWeight: 600, fontSize: 13 }}>
                基础信息
              </h3>
              <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 3 }}>
                必填字段标红星
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                gap: 14,
                padding: '12px 0',
                borderBottom: '1px solid var(--border)',
                alignItems: 'flex-start',
              }}
            >
              <div style={{ width: 140, flexShrink: 0, paddingTop: 6 }}>
                <div style={{ fontSize: 12.5, fontWeight: 500 }}>
                  书名<span style={{ color: 'var(--neg)', marginLeft: 3 }}>*</span>
                </div>
                <div
                  style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 3, lineHeight: 1.45 }}
                >
                  必填 · 最多 60 字
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <input className="input" placeholder="深渊之约" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 14, padding: '12px 0', alignItems: 'flex-start' }}>
              <div style={{ width: 140, flexShrink: 0, paddingTop: 6 }}>
                <div style={{ fontSize: 12.5, fontWeight: 500 }}>简介</div>
              </div>
              <div style={{ flex: 1 }}>
                <textarea className="input" rows={2} placeholder="简介…" />
              </div>
            </div>
          </div>
        </DSDemo>
      </DSBlock>
    </>
  );
}

// ============================================================
// Dialog / Drawer
// ============================================================
export function DSCDialog() {
  return (
    <>
      <DSHeader
        title="Dialog · Drawer"
        sub="Modal 中央弹窗用 .dialog；右侧抽屉用 .drawer · 重要详情用 drawer"
      />
      <DSBlock title="结构对比">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="card" style={{ overflow: 'hidden' }}>
            <div
              style={{
                padding: '10px 14px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--bg-subtle)',
              }}
            >
              <div style={{ fontWeight: 500, fontSize: 12.5 }}>Dialog</div>
              <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                中央 · max-width 560 · 信息流不长
              </div>
            </div>
            <div style={{ padding: 12, background: 'oklch(0.1 0.005 250 / 0.05)' }}>
              <div
                className="dialog"
                style={{
                  position: 'relative',
                  inset: 0,
                  width: '100%',
                  animation: 'none',
                  maxHeight: 'none',
                }}
              >
                <div style={{ padding: 14 }}>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>确认下架</div>
                  <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 4 }}>
                    下架后客户端不再展示该书…
                  </div>
                  <div className="hstack" style={{ marginTop: 12 }}>
                    <div style={{ flex: 1 }} />
                    <button type="button" className="btn btn-sm">
                      取消
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-accent"
                      style={{ marginLeft: 6 }}
                    >
                      确认
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card" style={{ overflow: 'hidden' }}>
            <div
              style={{
                padding: '10px 14px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--bg-subtle)',
              }}
            >
              <div style={{ fontWeight: 500, fontSize: 12.5 }}>Drawer</div>
              <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                右侧 · max-width 720 · 详情 / 编辑
              </div>
            </div>
            <div
              style={{
                padding: 12,
                position: 'relative',
                height: 180,
                overflow: 'hidden',
                background: 'oklch(0.1 0.005 250 / 0.05)',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: '70%',
                  background: 'var(--bg-elev)',
                  borderLeft: '1px solid var(--border)',
                  boxShadow: 'var(--shadow-lg)',
                  padding: 14,
                }}
              >
                <div style={{ fontWeight: 500, fontSize: 13 }}>区块详情</div>
                <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 4 }}>
                  书籍清单 / 多语言标题 / 推荐策略…
                </div>
              </div>
            </div>
          </div>
        </div>
      </DSBlock>
    </>
  );
}

// ============================================================
// Toast / Alert
// ============================================================
export function DSCToast() {
  return (
    <>
      <DSHeader title="Toast · Alert" sub="Toast 短暂提示 · Alert 内联横幅" />
      <DSBlock title="Toast">
        <DSDemo code={`toast("已保存", { tone: "pos", icon: "IconCheck" });`}>
          <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
            {[
              { tone: 'pos', text: '已保存《深渊之约》' },
              { tone: 'warn', text: '已隐藏 · 请填写原因' },
              { tone: 'neg', text: '操作失败 · 请重试' },
              { tone: 'info', text: '已同步 3 章' },
            ].map((t) => (
              <div key={t.tone} className="toast" style={{ position: 'static', animation: 'none' }}>
                <Icons.IconCheck size={12} style={{ color: `var(--${t.tone})` }} />
                <span>{t.text}</span>
              </div>
            ))}
          </div>
        </DSDemo>
      </DSBlock>
      <DSBlock title="内联 Alert">
        <DSDemo>
          <div className="vstack" style={{ gap: 6 }}>
            {[
              { tone: 'info', text: 'FAQ 已从 App 维度上升为全局配置' },
              { tone: 'warn', text: '下架后已购用户仍可继续阅读' },
              { tone: 'neg', text: '请填写英文原因 · 必填项' },
              { tone: 'pos', text: '已维护 4 / 6 种语言' },
            ].map((a) => (
              <div
                key={a.tone}
                className="card-flat"
                style={{
                  padding: '10px 12px',
                  background: `var(--${a.tone}-soft)`,
                  borderColor: 'transparent',
                }}
              >
                <div className="hstack" style={{ gap: 8, alignItems: 'flex-start' }}>
                  <Icons.IconInfo size={12} style={{ color: `var(--${a.tone})`, marginTop: 2 }} />
                  <span style={{ fontSize: 11.5, color: 'var(--fg)' }}>{a.text}</span>
                </div>
              </div>
            ))}
          </div>
        </DSDemo>
      </DSBlock>
    </>
  );
}

// ============================================================
// Multi-language field
// ============================================================
export function DSCMultilang() {
  // 原型示例用 window.__multiLang.useMultiLang 持有内部 state；
  // 冻结契约里 MultiLangFormRows 为受控组件，故文档示例用本地 state 演示。
  const [value, setValue] = useState<Record<string, string>>({ zh: '本周热推', en: 'Weekly Hot' });
  // 第二个 block 单独演示 OtherLangsField：自管「其他语言」values + enValue。
  // 可添加语言全集 = system enabled languages 剔除中英（useSysConfig 真数据派生，不读 mock seed）。
  const { data: sys } = useSysConfig();
  const otherLangs = sys.languages
    .filter((l) => l.enabled)
    .map((l) => l.language_code)
    .filter((code) => code !== 'zh' && code !== 'en');
  const [otherValues, setOtherValues] = useState<Record<string, string>>({});
  const enValue = 'Weekly Hot';
  return (
    <>
      <DSHeader
        title="多语言字段"
        sub="所有用户可见的文案统一用此模式：默认中文 + 英文必填 + 其他语言可手动添加。共享组件 ~/ui/MultiLangFormRows（受控：value + onChange）"
        badge={
          <span className="chip chip-accent" style={{ fontSize: 10 }}>
            核心模式
          </span>
        }
      />
      <DSBlock title="MultiLangFormRows · 一键三连">
        <DSDemo
          desc="自动渲染「中文* + 英文* + 其他语言」三个 FormRow"
          code={`const [value, setValue] = useState({ zh: "", en: "" });
<MultiLangFormRows
  label="区块标题"
  value={value}
  onChange={setValue}
/>`}
        >
          <div className="card-flat" style={{ padding: 14 }}>
            <MultiLangFormRows label="区块标题" value={value} onChange={setValue} />
          </div>
        </DSDemo>
      </DSBlock>
      <DSBlock title="OtherLangsField · 单独的其他语言区">
        <DSDemo
          desc="只渲染其他语言部分；中英必填字段自己写"
          code={`<OtherLangsField
  values={values} enValue={enValue}
  onChange={...} onAdd={...} onRemove={...}
  availableLangs={availableLangs}
/>`}
        >
          <div className="card-flat" style={{ padding: 14 }}>
            <OtherLangsField
              values={otherValues}
              enValue={enValue}
              onChange={(code, v) => setOtherValues((prev) => ({ ...prev, [code]: v }))}
              onAdd={(code) =>
                setOtherValues((prev) =>
                  prev[code] !== undefined ? prev : { ...prev, [code]: '' },
                )
              }
              onRemove={(code) =>
                setOtherValues((prev) => {
                  const next = { ...prev };
                  delete next[code];
                  return next;
                })
              }
              multiline={false}
              rows={2}
              availableLangs={otherLangs}
            />
          </div>
        </DSDemo>
      </DSBlock>
      <DSRule tone="do">
        所有用户可见文案（分类 / 标签 / 区块标题 / Banner / 推送 / FAQ / 下架原因）一律用此模式
      </DSRule>
      <DSRule tone="do">「从英文翻译」按钮会用 value.en 填充该语言（mock 翻译）</DSRule>
      <DSRule tone="note">服务端按用户最近使用语言返回内容，未维护语言 fallback 英文</DSRule>
    </>
  );
}

// ============================================================
// Status machine
// ============================================================
export function DSCStatus() {
  // 四档可见受众（dd#941）：不是状态机，是**受众单调收窄**的档位序列
  // PUBLIC ⊇ PAYING_ONLY ⊇ UNLOCKED_ONLY ⊇ ∅，任意两档之间可直接互切。
  const STATES = [
    { id: '公开', tone: 'pos', icon: 'IconCheckCircle', desc: '全体可见 · 进发现面' },
    { id: '付费可见', tone: 'info', icon: 'IconEye', desc: '仅已付费用户进发现面' },
    { id: '已购可见', tone: 'warn', icon: 'IconEye', desc: '不进发现面 · 已购可读' },
    { id: '下架', tone: 'neg', icon: 'IconBan', desc: '全拒 · 含已购已买断' },
  ] as const;
  return (
    <>
      <DSHeader
        title="可见受众档"
        sub="书籍受众档是核心样板：四档按受众单调收窄，三层配置（书组默认 / 单书 / 单书×单App）下层覆盖上层，落「下架」必弹原因"
      />
      <DSBlock title="四档可见受众（受众自左向右收窄）">
        <div className="card" style={{ padding: 20 }}>
          <div className="hstack" style={{ gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            {STATES.map((s, i, arr) => {
              const Icon = Icons[s.icon];
              return (
                <div key={s.id} className="hstack" style={{ gap: 16 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        background: `var(--${s.tone}-soft)`,
                        color: `var(--${s.tone})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto',
                      }}
                    >
                      <Icon size={22} />
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 500, marginTop: 6 }}>{s.id}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>{s.desc}</div>
                  </div>
                  {i < arr.length - 1 && (
                    <Icons.IconArrowRight size={14} style={{ color: 'var(--fg-faint)' }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </DSBlock>
      <DSBlock title="操作流程">
        <ol
          style={{
            fontSize: 12.5,
            color: 'var(--fg-muted)',
            lineHeight: 1.75,
            paddingLeft: 18,
            margin: 0,
          }}
        >
          <li>
            <b style={{ color: 'var(--fg)' }}>改到非「下架」档</b>：直接切换，无需原因
          </li>
          <li>
            <b style={{ color: 'var(--fg)' }}>改到「下架」</b>
            ：同一弹窗内追加原因表单，中英必填，多语言可加
          </li>
          <li>
            <b style={{ color: 'var(--fg)' }}>清除本层覆盖（继承上层）</b>
            ：与显式配「公开」是**两个不同动作** ——
            前者跟随上层变动，后者钉死为全体可见；上层是「下架」时清除覆盖同样要原因
          </li>
          <li>
            <b style={{ color: 'var(--fg)' }}>离开「下架」档</b>
            ：三层均非「下架」后才清空原因，否则保留
          </li>
        </ol>
      </DSBlock>
      <DSRule tone="do">所有档位变更记入操作日志</DSRule>
      <DSRule tone="do">破坏性切换（隐藏 / 下架）必有 ReasonModal</DSRule>
      <DSRule tone="dont">不要在表单里直接放 4-key 状态 seg — 用「当前状态 + 操作按钮」分离</DSRule>
    </>
  );
}
